import { useCallback, useMemo, useState } from 'react';

import { initialSelectedByField } from '@app/components/Modals/CombineClientsModal/data/constants';
import type {
  FieldKey,
  SelectedByField,
  TagOption,
  UseCombineClientsModalProps,
} from '@app/components/Modals/CombineClientsModal/types';
import { getModalWidth } from '@app/components/Modals/CombineClientsModal/utils';
import { useUserPrint } from '@app/hooks';
import type { ClientModel } from '@app/pages/DublicateClients/types';
import { useLazyGetAllTagsQuery } from '@app/services/rtkQuery/Tags/api';
import { useErrorHandler } from '@app/shared/hooks';
import type { NTags } from '@app/types';
import { getContrastYIQ } from '@app/utils';

export const useCombineClientsModal = ({
  combineClients,
  onMarkNotDuplicate,
  onSave,
}: UseCombineClientsModalProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const [tags, setTags] = useState<NTags.Tag[]>([]);
  const [
    getAllTags,
    {
      isLoading: isTagsLoading,
      isFetching: isTagsFetching,
      isUninitialized: isTagsUninitialized,
      isError: isTagsError,
      error: tagsError,
    },
  ] = useLazyGetAllTagsQuery();

  useErrorHandler([{ flag: isTagsError, error: tagsError }]);
  const [localClients, setLocalClients] = useState<ClientModel[]>([]);
  const [selectedByField, setSelectedByField] = useState<SelectedByField>(
    () => initialSelectedByField
  );

  const getAllTagsHandler = useCallback(async () => {
    try {
      const response = await getAllTags({ ActiveBranchId }).unwrap();

      setTags(response);
    } catch (error) {
      return [];
    }
  }, [getAllTags, ActiveBranchId]);

  const initialModal = useCallback(async () => {
    if (!combineClients) {
      setLocalClients([]);
      return;
    }

    await getAllTagsHandler();

    const clients = combineClients.Clients;
    setLocalClients(clients);

    const [first] = clients;

    const initial: SelectedByField = {
      ClientName: first?.ClientId ?? null,
      Phone: clients.find((c) => c.Phone)?.ClientId ?? null,
      Email: clients.find((c) => c.Email)?.ClientId ?? null,
      VK: clients.find((c) => c.VK)?.ClientId ?? null,
      Telegram: clients.find((c) => c.Telegram)?.ClientId ?? null,
      Instagram: clients.find((c) => c.Instagram)?.ClientId ?? null,
      Tags: clients.find((c) => c.Tags && c.Tags.length)?.ClientId ?? null,
    };

    setSelectedByField(initial);
  }, [combineClients, getAllTagsHandler]);

  const handleSelectField = useCallback((field: FieldKey, clientId: number) => {
    setSelectedByField((prev) => ({
      ...prev,
      [field]: prev[field] === clientId ? null : clientId,
    }));
  }, []);

  const handleNotDuplicate = useCallback(
    (clientId: number) => {
      setLocalClients((prev) => prev.filter((c) => c.ClientId !== clientId));
      onMarkNotDuplicate(clientId);
    },
    [onMarkNotDuplicate]
  );

  const totalRecords = useMemo(
    () => localClients.reduce((acc, c) => acc + c.RecordsCount, 0),
    [localClients]
  );

  const totalSales = useMemo(
    () => localClients.reduce((acc, c) => acc + c.SalesCount, 0),
    [localClients]
  );

  const getFieldValue = useCallback(
    (field: FieldKey): string | null => {
      const clientId = selectedByField[field];

      if (!clientId) {
        return null;
      }

      const client = localClients.find((c) => c.ClientId === clientId);

      if (!client) {
        return null;
      }

      switch (field) {
        case 'ClientName':
          return client.ClientName;
        case 'Phone':
          return client.Phone;
        case 'Email':
          return client.Email;
        case 'VK':
          return client.VK;
        case 'Telegram':
          return client.Telegram;
        case 'Instagram':
          return client.Instagram;
        case 'Tags':
          return (client.Tags ?? []).map((id) => `Tag #${id}`).join(', ');
        default:
          return null;
      }
    },
    [localClients, selectedByField]
  );

  const resultClient: ClientModel | null = useMemo(() => {
    if (!localClients.length) return null;
    const [base] = localClients;

    return {
      ...base,
      ClientName: getFieldValue('ClientName') ?? base.ClientName,
      Phone: getFieldValue('Phone'),
      Email: getFieldValue('Email'),
      VK: getFieldValue('VK'),
      Telegram: getFieldValue('Telegram'),
      Instagram: getFieldValue('Instagram'),
      Tags: (() => {
        const clientId = selectedByField.Tags;
        if (!clientId) return base.Tags ?? null;
        const client = localClients.find((c) => c.ClientId === clientId);
        return client?.Tags ?? null;
      })(),
      RecordsCount: totalRecords,
      SalesCount: totalSales,
    };
  }, [localClients, selectedByField, totalRecords, totalSales, getFieldValue]);

  const handleSaveClick = useCallback(() => {
    if (!resultClient || !combineClients) return;
    onSave(resultClient);
  }, [resultClient, combineClients, onSave]);

  const clientsCount = useMemo(
    () => localClients.length || combineClients?.Clients.length || 0,
    [localClients, combineClients]
  );
  const modalWidth = useMemo(
    () => getModalWidth(clientsCount || 2),
    [clientsCount]
  );

  const handleSelectAllFromClient = useCallback((clientId: number) => {
    setSelectedByField((prev) => {
      const isAlreadySelected = Object.values(prev).every(
        (value) => value === clientId
      );

      if (isAlreadySelected) {
        const resetFields = { ...prev };
        Object.keys(resetFields).forEach((key) => {
          if (resetFields[key as FieldKey] === clientId) {
            resetFields[key as FieldKey] = null;
          }
        });
        return resetFields;
      }

      const updated: SelectedByField = { ...prev };
      Object.keys(updated).forEach((key) => {
        updated[key as FieldKey] = clientId;
      });

      return updated;
    });
  }, []);

  const isLoading = useMemo(
    () => isTagsLoading || isTagsFetching || isTagsUninitialized,
    [isTagsLoading, isTagsFetching, isTagsUninitialized]
  );

  const tagOptions: TagOption[] = useMemo(
    () =>
      tags.map(({ Id, Name, Color, IconId }) => ({
        value: Id,
        label: Name,
        color: getContrastYIQ(Color),
        backgroundColor: Color,
        icon: IconId,
      })),
    [tags]
  );
  return {
    initialModal,
    localClients,
    selectedByField,
    handleSelectField,
    handleNotDuplicate,
    handleSaveClick,
    resultClient,
    modalWidth,
    handleSelectAllFromClient,
    getAllTagsHandler,
    tagOptions,
    isLoading,
  };
};
